/**
 * Pantalla de Salas (V1)
 * - Listar salas del usuario
 * - Crear sala
 * - Ver invitaciones pendientes
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Typography,
    Avatar,
    Divider,
    useTheme,
    Grid,
    Paper,
    Fade,
    Zoom,
    Tooltip,
    IconButton
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import GroupsIcon from "@mui/icons-material/Groups";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { motion, AnimatePresence } from "framer-motion";

import { obtenerSalasDelUsuario } from "../api/salas";
import CrearSalaModal from "../components/CrearSalaModal";
import InvitacionesPendientes from "../components/InvitacionesPendientes";

const MotionCard = motion(Card);

export default function Salas() {
    const navigate = useNavigate();
    const theme = useTheme();

    // Datos
    const [salas, setSalas] = useState([]);

    // Estados UI
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(false);

    // Modal crear sala
    const [openCrear, setOpenCrear] = useState(false);

    async function cargarSalas() {
        setCargando(true);
        setError(false);

        try {
            const data = await obtenerSalasDelUsuario();
            setSalas(Array.isArray(data) ? data : []);
        } catch (err) {
            if (err.response?.status === 403) {
                setSalas([]);
            } else {
                setError(true);
            }
        } finally {
            setCargando(false);
        }
    }

    useEffect(() => {
        cargarSalas();
    }, []);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, mx: 'auto' }}>
            {/* Header */}
            <Fade in timeout={500}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 4 }}
                >
                    <Box>
                        <Typography variant="h3" sx={{ fontWeight: 950, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
                            <GroupsIcon sx={{ fontSize: 40 }} /> Mis Salas
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.7, fontWeight: 500 }}>
                            Espacios compartidos para organizar listas con tu familia o amigos.
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenCrear(true)}
                        sx={{ borderRadius: 3, fontWeight: 700, px: 3, textTransform: 'none', boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)' }}
                    >
                        Nueva sala
                    </Button>
                </Stack>
            </Fade>

            {/* Invitaciones */}
            <Box sx={{ mb: 4 }}>
                <InvitacionesPendientes />
            </Box>

            {/* Estados */}
            {cargando ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
                    <CircularProgress size={60} thickness={4} />
                    <Typography sx={{ opacity: 0.6, fontWeight: 600 }}>Cargando tus salas...</Typography>
                </Box>
            ) : error ? (
                <Card sx={{ borderRadius: 4, bgcolor: 'error.main' + '05', border: '1px solid', borderColor: 'error.main' }}>
                    <CardContent>
                        <Typography color="error" sx={{ fontWeight: 900 }}>Error</Typography>
                        <Typography variant="body2">No se pudieron cargar las salas. Por favor, intenta de nuevo.</Typography>
                    </CardContent>
                </Card>
            ) : salas.length === 0 ? (
                <Zoom in>
                    <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 4, bgcolor: 'rgba(0,0,0,0.02)', border: '2px dashed', borderColor: 'divider' }}>
                        <GroupsIcon sx={{ fontSize: 80, color: 'divider', mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Aún no perteneces a ninguna sala</Typography>
                        <Typography variant="body1" sx={{ opacity: 0.6, mb: 4 }}>Las salas te permiten compartir listas y chatear con otros usuarios.</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenCrear(true)} sx={{ borderRadius: 2, fontWeight: 700 }}>
                            Crear mi primera sala
                        </Button>
                    </Paper>
                </Zoom>
            ) : (
                <Grid container spacing={3}>
                    <AnimatePresence>
                        {salas.map((sala, idx) => (
                            <Grid item xs={12} sm={6} key={sala.idsala}>
                                <MotionCard
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: idx * 0.1 }}
                                    whileHover={{ y: -5, boxShadow: '0 12px 40px rgba(0,0,0,0.12)' }}
                                    sx={{ 
                                        borderRadius: 4, 
                                        border: '1px solid', 
                                        borderColor: 'divider',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                        background: `linear-gradient(135deg, #fff 0%, ${theme.palette.primary.main}05 100%)`
                                    }}
                                >
                                    <CardContent sx={{ p: 3, flex: 1 }}>
                                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }} noWrap>
                                                    {sala.nombre}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                opacity: 0.8, 
                                                mb: 3, 
                                                height: '3em', 
                                                overflow: 'hidden',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}
                                        >
                                            {sala.descripcion || 'Sin descripción'}
                                        </Typography>

                                        <Divider sx={{ mb: 2, opacity: 0.6 }} />

                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={() => navigate(`/salas/${sala.idsala}`)}
                                            sx={{ 
                                                borderRadius: 2, 
                                                fontWeight: 800, 
                                                textTransform: 'none',
                                                borderWidth: 2,
                                                '&:hover': { borderWidth: 2 }
                                            }}
                                        >
                                            Entrar a la sala
                                        </Button>
                                    </CardContent>
                                </MotionCard>
                            </Grid>
                        ))}
                    </AnimatePresence>
                </Grid>
            )}

            {/* Modal crear sala */}
            <CrearSalaModal
                open={openCrear}
                onClose={() => setOpenCrear(false)}
                onCreated={cargarSalas}
            />
        </Box>
    );
}
